import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Check,
  X,
  Warning,
  CurrencyCircleDollar,
  Receipt,
  Hourglass,
  CaretDown,
  CaretUp,
  NotePencil,
  Info,
  ArrowsClockwise,
  CalendarPlus,
  ArrowRight,
} from "@phosphor-icons/react";
import {
  selectClinicId,
  selectTherapistId,
  selectTherapistName,
  selectIsAdmin,
  selectCanCreateGreenInvoice,
  selectIsDigitalInvoiceActive,
} from "../../../store/slices/authSlice";
import {
  useCancelAppointmentMutation,
  useUpdateAppointmentMutation,
  useUpdateRecurringAppointmentsMutation,
  calendarApi,
} from "../../../store/services/calendarApi";
import {
  useCreateTransactionMutation,
  useUpdateTransactionMutation,
  financeApi,
} from "../../../store/services/financeApi";
import {
  getTransactionByAppointment,
  getTreatmentByAppointment,
  getRecurringGroupAppointments,
  TRANSACTION_STATUS,
} from "../../../firebase";
import InvoiceQuickModal from "../../morning/InvoiceQuickModal";
import DigitalInvoiceQuickModal from "../../digitalInvoice/DigitalInvoiceQuickModal";
import DatePicker from "../../ui/DatePicker";
import ModalCloseButton from "../../ui/ModalCloseButton";
import LoaderLoop from "../../shared/loader/LoaderLoop";
import "./useCancelAppointmentFlow.css";
import { getIsraelDateString } from "../../../utils/dateUtils";

/**
 * Shared state-machine for the "cancel appointment" flow.
 *
 * High-level steps (see `CANCEL_STEPS` below):
 *   1. CONFIRM       - confirm + collapsible reason textarea + warnings for
 *                      linked treatment summary / linked payment
 *   2. TX_DECISION   - only when the linked financial transaction is in a
 *                      PAID-family status (PAID / INVOICE_*) — lets the
 *                      therapist choose how to handle the existing payment
 *   3. FEE_ASK       - for non-paid cases: does the therapist want to record
 *                      a cancellation fee?
 *   4. FEE_AMOUNT    - enter the fee amount
 *   5. FEE_DETAILS   - payment method + date (or "save as pending collection")
 *   6. FEE_INVOICE   - optionally issue an invoice (when provider is active)
 *
 * The flow is rendered as a modal so all entry points (pending carousel,
 * appointment-modal footer, calendar context menu) share a single code path.
 *
 * Error Code: CK-7054 (mutation) / CK-3410x (flow-local)
 *
 * @returns {{
 *   startCancel: (appointment: object) => void,
 *   isCancelling: boolean,
 *   cancelFlowNode: React.ReactNode,
 * }}
 */
export const CANCEL_STEPS = {
  CONFIRM: "confirm",
  // Series-only alternative to cancelling: push this meeting and every later
  // one a week forward instead of losing the session.
  POSTPONE: "postpone",
  POSTPONE_DONE: "postpone_done",
  TX_DECISION: "tx_decision",
  FEE_ASK: "fee_ask",
  FEE_AMOUNT: "fee_amount",
  FEE_DETAILS: "fee_details",
  FEE_INVOICE: "fee_invoice",
};

const PAYMENT_METHODS = [
  { value: "cash", label: "מזומן" },
  { value: "credit", label: "אשראי" },
  { value: "transfer", label: "העברה" },
  { value: "check", label: "צ׳ק" },
  { value: "bit", label: "אפליקציה", subLabel: "ביט/פייבוקס/אחר" },
  { value: "other", label: "אחר" },
];

const MAX_REASON_LENGTH = 300;

// Transaction statuses that mean "money was effectively received" — these are
// the only cases where we ask the therapist how to handle the linked payment
// before cancelling.
const PAID_FAMILY_STATUSES = new Set([
  TRANSACTION_STATUS.PAID,
  TRANSACTION_STATUS.INVOICE_MANUAL,
  TRANSACTION_STATUS.INVOICE_AUTO,
]);

function isPaidFamily(tx) {
  if (!tx) return false;
  return PAID_FAMILY_STATUSES.has(tx.status);
}

function describeTxStatus(tx) {
  if (!tx) return "";
  switch (tx.status) {
    case TRANSACTION_STATUS.PAID:
      return "שולם";
    case TRANSACTION_STATUS.INVOICE_MANUAL:
      return "שולם + חשבונית";
    case TRANSACTION_STATUS.INVOICE_AUTO:
      return "שולם + חשבונית (אוטומטי)";
    case TRANSACTION_STATUS.PENDING:
    case TRANSACTION_STATUS.PENDING_COLLECTION:
      return "ממתין לתשלום";
    case TRANSACTION_STATUS.EXPECTED:
      return "צפי הכנסה";
    case TRANSACTION_STATUS.REFUNDED:
      return "זוכה";
    case TRANSACTION_STATUS.CANCELLED:
      return "בוטל";
    default:
      return tx.status || "";
  }
}

function formatAppointmentWhen(appointment) {
  if (!appointment?.startTime) return "";
  try {
    const date = new Date(appointment.startTime);
    return date.toLocaleDateString("he-IL", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function formatDateOnly(date) {
  if (!date) return "";
  return date.toLocaleDateString("he-IL", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatTimeOnly(date) {
  if (!date) return "";
  return date.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" });
}

/**
 * What a "push it a week" would do to this meeting and the rest of its series.
 *
 * Mirrors the server-side `postpone_week` scope exactly (see
 * `updateRecurringAppointments` in firebase.js): the meeting itself moves +7
 * days, and every LATER occurrence that hasn't happened yet slides 7 days too,
 * keeping its own weekday and hour. Cancelled / completed / past occurrences
 * are never touched.
 *
 * Returns null when the appointment isn't part of a series.
 */
function buildPostponePlan(appointment, seriesMeetings) {
  if (!appointment?.isRecurring || !appointment.startTime) return null;

  const originalStart = new Date(appointment.startTime);
  const newStart = new Date(originalStart);
  newStart.setDate(newStart.getDate() + 7);
  const durationMs = appointment.endTime
    ? new Date(appointment.endTime).getTime() - originalStart.getTime()
    : 0;
  const newEnd = new Date(newStart.getTime() + durationMs);

  const originalStartMs = originalStart.getTime();
  const nowMs = Date.now();
  const later = (seriesMeetings || []).filter((apt) => {
    if (apt.id === appointment.id) return false;
    if (apt.isDeleted === true) return false;
    if (apt.status === "cancelled" || apt.status === "completed") return false;
    const startMs = new Date(apt.startTime).getTime();
    return startMs > originalStartMs && startMs > nowMs;
  });

  let lastAfter = null;
  if (later.length > 0) {
    lastAfter = new Date(
      Math.max(...later.map((apt) => new Date(apt.startTime).getTime()))
    );
    lastAfter.setDate(lastAfter.getDate() + 7);
  }

  return {
    newStart,
    newEnd,
    laterCount: later.length,
    lastAfter,
    // Pushing something into the past helps nobody.
    isPossible: newStart.getTime() > nowMs,
  };
}

function formatAmount(value) {
  const numeric = parseFloat(value);
  if (!Number.isFinite(numeric)) return "";
  return `₪${numeric.toLocaleString("he-IL")}`;
}

export default function useCancelAppointmentFlow({ onCancelled, onPostponed } = {}) {
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const therapistId = useSelector(selectTherapistId);
  const therapistName = useSelector(selectTherapistName);
  const isAdmin = useSelector(selectIsAdmin);
  const isUsingGreenInvoice = useSelector(selectCanCreateGreenInvoice);
  const isDigitalInvoiceActive = useSelector(selectIsDigitalInvoiceActive);
  const canCreateInvoices = isUsingGreenInvoice || isDigitalInvoiceActive;

  const [cancelAppointment, { isLoading: isCancelMutating }] =
    useCancelAppointmentMutation();
  const [updateAppointment, { isLoading: isUpdateMutating }] =
    useUpdateAppointmentMutation();
  const [updateRecurringAppointments, { isLoading: isSeriesMutating }] =
    useUpdateRecurringAppointmentsMutation();
  const [updateTransaction, { isLoading: isTxMutating }] =
    useUpdateTransactionMutation();
  const [createTransaction, { isLoading: isTxCreating }] =
    useCreateTransactionMutation();

  // Single active flow at a time — sufficient because the UI only lets the
  // user cancel one appointment at a time (carousel card X, modal footer).
  const [flow, setFlow] = useState(null);
  // Invoice modal state (reuses the same pattern as the carousel).
  const [invoiceState, setInvoiceState] = useState({
    open: false,
    appointment: null,
    amount: null,
    paymentMethod: null,
  });

  const isCancelling =
    !!flow ||
    isCancelMutating ||
    isUpdateMutating ||
    isSeriesMutating ||
    isTxMutating ||
    isTxCreating;

  const invalidateFinanceTags = useCallback(
    (patientId) => {
      dispatch(
        calendarApi.util.invalidateTags([
          { type: "Finance", id: "STATS" },
          { type: "Finance", id: "PENDING_PAYMENTS" },
          { type: "Finance", id: "UPCOMING" },
          ...(patientId
            ? [{ type: "Finance", id: `PATIENT_${patientId}` }]
            : []),
        ])
      );
      dispatch(
        financeApi.util.invalidateTags([
          { type: "TransactionStats", id: "MONTHLY" },
          { type: "PendingCollection", id: "LIST" },
          { type: "Transactions", id: "EXPECTED" },
          { type: "Transactions", id: "PAID_HISTORY" },
          { type: "KanbanTransactions", id: "LIST" },
          ...(patientId
            ? [{ type: "Transactions", id: `PATIENT_${patientId}` }]
            : []),
        ])
      );
    },
    [dispatch]
  );

  const closeFlow = useCallback(() => setFlow(null), []);

  const startFlow = useCallback((appointment, step) => {
    if (!appointment?.id) return;
    const defaultDate = appointment.startTime
      ? getIsraelDateString(new Date(appointment.startTime))
      : getIsraelDateString(new Date());
    setFlow({
      step,
      // Opened straight into the postpone plan (from a delete dialog) — there
      // is no cancel step behind it to go back to.
      postponeOnly: step === CANCEL_STEPS.POSTPONE,
      appointment,
      amount: appointment.paymentAmount ?? "",
      method: "cash",
      date: defaultDate,
      isWorking: false,
      error: null,
      // Reason UI state
      reason: "",
      reasonOpen: false,
      // Pre-load state: undefined = still loading, null = no linked doc
      linkedTransaction: undefined,
      linkedTreatment: undefined,
      // Series siblings — only loaded for recurring appointments, so the
      // postpone option can state exactly how many meetings move.
      seriesMeetings: undefined,
      preloadError: null,
      // Series shift refused by the server (double-booking) — waivable.
      postponeConflicts: [],
      postponeResult: null,
    });
  }, []);

  const startCancel = useCallback(
    (appointment) => startFlow(appointment, CANCEL_STEPS.CONFIRM),
    [startFlow]
  );

  // Entry point for "push it a week instead" offered from the delete dialogs —
  // opens the same explained plan screen without going through cancel.
  const startPostpone = useCallback(
    (appointment) => {
      if (appointment?.isRecurring !== true) return;
      startFlow(appointment, CANCEL_STEPS.POSTPONE);
    },
    [startFlow]
  );

  const updateFlow = useCallback((patch) => {
    setFlow((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  // Pre-load the linked transaction + treatment summary for the current
  // appointment so the CONFIRM step can render accurate warnings and decide
  // whether to route through TX_DECISION. Runs once per flow instance.
  useEffect(() => {
    if (!flow?.appointment?.id || !clinicId) return;
    // Only fetch once — driven by undefined sentinel set in startCancel.
    if (
      flow.linkedTransaction !== undefined &&
      flow.linkedTreatment !== undefined &&
      flow.seriesMeetings !== undefined
    ) {
      return;
    }

    const groupId = flow.appointment.isRecurring
      ? flow.appointment.recurringGroupId
      : null;

    let cancelled = false;
    (async () => {
      try {
        const [tx, treatment, series] = await Promise.all([
          getTransactionByAppointment(clinicId, flow.appointment.id),
          getTreatmentByAppointment(clinicId, flow.appointment.id),
          groupId
            ? getRecurringGroupAppointments(clinicId, groupId, therapistId, isAdmin)
            : Promise.resolve([]),
        ]);
        if (cancelled) return;
        setFlow((prev) => {
          if (!prev || prev.appointment.id !== flow.appointment.id) return prev;
          return {
            ...prev,
            linkedTransaction: tx || null,
            linkedTreatment: treatment || null,
            seriesMeetings: series || [],
          };
        });
      } catch (err) {
        console.error("[CK-3414] Error preloading cancel flow context:", {
          errorCode: "CK-3414",
          errorMessage: err.message,
          appointmentId: flow.appointment.id,
          timestamp: new Date().toISOString(),
        });
        if (cancelled) return;
        setFlow((prev) => {
          if (!prev || prev.appointment.id !== flow.appointment.id) return prev;
          return {
            ...prev,
            linkedTransaction: prev.linkedTransaction ?? null,
            linkedTreatment: prev.linkedTreatment ?? null,
            seriesMeetings: prev.seriesMeetings ?? [],
            preloadError: err.message || "שגיאה בטעינת מידע מקושר",
          };
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    flow?.appointment?.id,
    flow?.appointment?.isRecurring,
    flow?.appointment?.recurringGroupId,
    flow?.linkedTransaction,
    flow?.linkedTreatment,
    flow?.seriesMeetings,
    clinicId,
    therapistId,
    isAdmin,
  ]);

  // Core cancel operation — shared by multiple step handlers.
  //
  // Accepts an explicit `transactionAction` so the caller (CONFIRM for
  // regular cases, TX_DECISION for PAID-family cases) controls how the
  // linked financial_transactions row is touched. Returns `true` on success.
  const performCancel = useCallback(
    async ({ transactionAction }) => {
      if (!flow) return false;
      const reasonTrimmed = (flow.reason || "").trim().slice(0, MAX_REASON_LENGTH);
      updateFlow({ isWorking: true, error: null });
      try {
        await cancelAppointment({
          clinicId,
          appointmentId: flow.appointment.id,
          cancelledBy: "therapist",
          keepPaymentNotify: false,
          cancellationReason: reasonTrimmed || null,
          transactionAction,
          patientId: flow.appointment.patientId,
          patientName: flow.appointment.patientName,
          therapistId,
          therapistName,
        }).unwrap();

        invalidateFinanceTags(flow.appointment.patientId);

        if (typeof onCancelled === "function") {
          try {
            onCancelled({
              ...flow.appointment,
              cancellationReason: reasonTrimmed || null,
            });
          } catch (cbError) {
            console.error("[CK-3410c] onCancelled callback failed:", cbError);
          }
        }
        return true;
      } catch (error) {
        console.error("[CK-3410] Error cancelling appointment via hook:", {
          errorCode: "CK-3410",
          errorMessage: error.message,
          appointmentId: flow.appointment.id,
          transactionAction,
          timestamp: new Date().toISOString(),
        });
        updateFlow({
          isWorking: false,
          error: error.message || "שגיאה בביטול הפגישה",
        });
        return false;
      }
    },
    [
      flow,
      cancelAppointment,
      clinicId,
      therapistId,
      therapistName,
      invalidateFinanceTags,
      onCancelled,
      updateFlow,
    ]
  );

  /**
   * Step 1 → 2: actually cancel the appointment, or route to TX_DECISION
   * first when the linked transaction is in a PAID-family status.
   */
  const handleConfirmCancel = useCallback(async () => {
    if (!flow) return;
    // If the linked transaction has already been paid/invoiced, don't cancel
    // yet — the therapist needs to decide what to do with it first.
    if (isPaidFamily(flow.linkedTransaction)) {
      updateFlow({ step: CANCEL_STEPS.TX_DECISION, error: null });
      return;
    }
    const ok = await performCancel({ transactionAction: "cancel" });
    if (ok) {
      updateFlow({ step: CANCEL_STEPS.FEE_ASK, isWorking: false });
    }
  }, [flow, performCancel, updateFlow]);

  /**
   * The alternative to cancelling: push this meeting and every later occurrence
   * a week forward. The series keeps all of its meetings and simply ends a week
   * later. Nothing is cancelled and no payment is touched.
   *
   * Error Code: CK-3418
   */
  const handlePostponeWeek = useCallback(
    async ({ allowConflicts = false } = {}) => {
      if (!flow) return;
      const plan = buildPostponePlan(flow.appointment, flow.seriesMeetings);
      if (!plan?.isPossible) return;

      updateFlow({ isWorking: true, error: null, postponeConflicts: [] });
      try {
        const result = await updateRecurringAppointments({
          clinicId,
          appointmentId: flow.appointment.id,
          updateData: {
            startTime: plan.newStart.toISOString(),
            endTime: plan.newEnd.toISOString(),
          },
          scope: "postpone_week",
          patientName: flow.appointment.patientName,
          therapistId,
          therapistName,
          allowConflicts,
        }).unwrap();

        // Refused: the new slots collide with other meetings. Nothing written —
        // show what clashes and let the therapist decide.
        if (result?.hasConflicts) {
          updateFlow({
            isWorking: false,
            postponeConflicts: result.conflicts || [],
            error: result.waivable
              ? null
              : "המועד החדש מתנגש עם מפגש אחר באותה סדרה",
          });
          return;
        }

        invalidateFinanceTags(flow.appointment.patientId);
        updateFlow({
          step: CANCEL_STEPS.POSTPONE_DONE,
          isWorking: false,
          postponeConflicts: [],
          postponeResult: {
            newStart: plan.newStart.toISOString(),
            shiftedCount: result?.shiftedCount ?? 0,
            lastAfter: plan.lastAfter ? plan.lastAfter.toISOString() : null,
          },
        });

        if (typeof onPostponed === "function") {
          try {
            onPostponed({ ...flow.appointment, newStartTime: plan.newStart.toISOString() });
          } catch (cbError) {
            console.error("[CK-3416b] onPostponed callback failed:", cbError);
          }
        }
      } catch (error) {
        console.error("[CK-3418] Error postponing series by a week:", {
          errorCode: "CK-3418",
          errorMessage: error.message,
          appointmentId: flow.appointment.id,
          timestamp: new Date().toISOString(),
        });
        updateFlow({
          isWorking: false,
          error: error.message || "שגיאה בדחיית המפגשים",
        });
      }
    },
    [
      flow,
      updateRecurringAppointments,
      clinicId,
      therapistId,
      therapistName,
      invalidateFinanceTags,
      onPostponed,
      updateFlow,
    ]
  );

  // TX_DECISION handlers — each cancels with a different transactionAction
  // and closes the flow afterwards (no fee step because the existing PAID
  // transaction already covers the financial bookkeeping).
  const handleTxKeep = useCallback(async () => {
    const ok = await performCancel({ transactionAction: "keep" });
    if (ok) closeFlow();
  }, [performCancel, closeFlow]);

  const handleTxConvertToFee = useCallback(async () => {
    const ok = await performCancel({
      transactionAction: "convertToCancellationFee",
    });
    if (ok) {
      // Mirror `cancellationFeeRecorded` on the appointment so the banner
      // reads "נרשמו דמי ביטול" like the regular fee flow.
      try {
        await updateAppointment({
          clinicId,
          appointmentId: flow.appointment.id,
          appointmentData: { cancellationFeeRecorded: true },
          therapistId,
          therapistName,
          patientName: flow.appointment.patientName,
        }).unwrap();
      } catch (err) {
        console.error("[CK-3415] Error flagging cancellationFeeRecorded:", {
          errorCode: "CK-3415",
          errorMessage: err.message,
          appointmentId: flow.appointment.id,
          timestamp: new Date().toISOString(),
        });
      }
      closeFlow();
    }
  }, [
    performCancel,
    updateAppointment,
    clinicId,
    flow,
    therapistId,
    therapistName,
    closeFlow,
  ]);

  const handleTxCancel = useCallback(async () => {
    const ok = await performCancel({ transactionAction: "cancel" });
    if (ok) closeFlow();
  }, [performCancel, closeFlow]);

  const handleFeeYes = useCallback(() => {
    updateFlow({ step: CANCEL_STEPS.FEE_AMOUNT });
  }, [updateFlow]);

  const handleFeeNo = useCallback(() => {
    closeFlow();
  }, [closeFlow]);

  const handleAmountConfirm = useCallback(() => {
    if (!flow) return;
    const numeric = parseFloat(flow.amount);
    if (!flow.amount || Number.isNaN(numeric) || numeric <= 0) {
      updateFlow({ error: "יש להזין סכום תקין" });
      return;
    }
    updateFlow({ step: CANCEL_STEPS.FEE_DETAILS, error: null });
  }, [flow, updateFlow]);

  /**
   * Writes the cancellation fee as a paid transaction.
   *
   * Error Code: CK-3411
   */
  const handleDetailsConfirm = useCallback(async () => {
    if (!flow) return;
    const numeric = parseFloat(flow.amount);
    if (!numeric) {
      updateFlow({ step: CANCEL_STEPS.FEE_AMOUNT, error: "יש להזין סכום תקין" });
      return;
    }
    const paymentDateIso = flow.date
      ? new Date(flow.date).toISOString()
      : new Date().toISOString();

    updateFlow({ isWorking: true, error: null });
    try {
      await updateAppointment({
        clinicId,
        appointmentId: flow.appointment.id,
        appointmentData: {
          isTherapistUpdatedPaymentStatus: true,
          paymentStatus: "paid",
          paymentAmount: numeric,
          actualPaidAmount: numeric,
          paymentMethod: flow.method || "cash",
          paymentDate: paymentDateIso,
          cancellationFeeRecorded: true,
        },
        therapistId,
        therapistName,
        patientName: flow.appointment.patientName,
      }).unwrap();

      try {
        const transaction = await getTransactionByAppointment(
          clinicId,
          flow.appointment.id
        );
        if (transaction) {
          await updateTransaction({
            clinicId,
            transactionId: transaction.id,
            updateData: {
              status: TRANSACTION_STATUS.PAID,
              amount: numeric,
              paymentMethod: flow.method || "cash",
              actualPaymentDate: paymentDateIso,
              isCancellationFee: true,
            },
            therapistId,
            therapistName,
            patientName: flow.appointment.patientName,
            patientId: flow.appointment.patientId,
          }).unwrap();
        } else {
          await createTransaction({
            clinicId,
            transactionData: {
              therapistId,
              appointmentId: flow.appointment.id,
              patientId: flow.appointment.patientId,
              patientName: flow.appointment.patientName,
              amount: numeric,
              paymentMethod: flow.method || "cash",
              status: TRANSACTION_STATUS.PAID,
              expectedPaymentDate: flow.appointment.startTime || null,
              actualPaymentDate: paymentDateIso,
              description: `דמי ביטול — ${flow.appointment.patientName || ""}`.trim(),
              isCancellationFee: true,
            },
            therapistId,
            therapistName,
          }).unwrap();
        }
        invalidateFinanceTags(flow.appointment.patientId);
      } catch (txError) {
        console.error("[CK-3411a] Error writing tx for cancellation fee:", {
          errorCode: "CK-3411a",
          errorMessage: txError.message,
          appointmentId: flow.appointment.id,
          timestamp: new Date().toISOString(),
        });
      }

      if (canCreateInvoices) {
        updateFlow({ step: CANCEL_STEPS.FEE_INVOICE, isWorking: false });
      } else {
        closeFlow();
      }
    } catch (error) {
      console.error("[CK-3411] Error recording cancellation fee:", {
        errorCode: "CK-3411",
        errorMessage: error.message,
        appointmentId: flow.appointment.id,
        timestamp: new Date().toISOString(),
      });
      updateFlow({
        isWorking: false,
        error: error.message || "שגיאה בשמירת התשלום",
      });
    }
  }, [
    flow,
    clinicId,
    therapistId,
    therapistName,
    updateAppointment,
    updateTransaction,
    createTransaction,
    invalidateFinanceTags,
    canCreateInvoices,
    closeFlow,
    updateFlow,
  ]);

  /**
   * Alternative finish for FEE_DETAILS — record as pending collection.
   *
   * Error Code: CK-3413
   */
  const handleDetailsPendingCollection = useCallback(async () => {
    if (!flow) return;
    const numeric = parseFloat(flow.amount);
    if (!numeric) {
      updateFlow({ step: CANCEL_STEPS.FEE_AMOUNT, error: "יש להזין סכום תקין" });
      return;
    }

    updateFlow({ isWorking: true, error: null });
    try {
      await updateAppointment({
        clinicId,
        appointmentId: flow.appointment.id,
        appointmentData: {
          isTherapistUpdatedPaymentStatus: true,
          paymentStatus: "pending",
          paymentAmount: numeric,
          cancellationFeeRecorded: true,
        },
        therapistId,
        therapistName,
        patientName: flow.appointment.patientName,
      }).unwrap();

      try {
        const transaction = await getTransactionByAppointment(
          clinicId,
          flow.appointment.id
        );
        if (transaction) {
          await updateTransaction({
            clinicId,
            transactionId: transaction.id,
            updateData: {
              status: TRANSACTION_STATUS.PENDING_COLLECTION,
              amount: numeric,
              isCancellationFee: true,
            },
            therapistId,
            therapistName,
            patientName: flow.appointment.patientName,
            patientId: flow.appointment.patientId,
          }).unwrap();
        } else {
          await createTransaction({
            clinicId,
            transactionData: {
              therapistId,
              appointmentId: flow.appointment.id,
              patientId: flow.appointment.patientId,
              patientName: flow.appointment.patientName,
              amount: numeric,
              status: TRANSACTION_STATUS.PENDING_COLLECTION,
              expectedPaymentDate: flow.appointment.startTime || null,
              description: `דמי ביטול — ${flow.appointment.patientName || ""}`.trim(),
              isCancellationFee: true,
            },
            therapistId,
            therapistName,
          }).unwrap();
        }
        invalidateFinanceTags(flow.appointment.patientId);
      } catch (txError) {
        console.error("[CK-3413a] Error writing tx to pending_collection:", {
          errorCode: "CK-3413a",
          errorMessage: txError.message,
          appointmentId: flow.appointment.id,
          timestamp: new Date().toISOString(),
        });
      }

      closeFlow();
    } catch (error) {
      console.error("[CK-3413] Error recording cancellation fee as pending:", {
        errorCode: "CK-3413",
        errorMessage: error.message,
        appointmentId: flow.appointment.id,
        timestamp: new Date().toISOString(),
      });
      updateFlow({
        isWorking: false,
        error: error.message || "שגיאה בשמירת דמי הביטול",
      });
    }
  }, [
    flow,
    clinicId,
    therapistId,
    therapistName,
    updateAppointment,
    updateTransaction,
    createTransaction,
    invalidateFinanceTags,
    closeFlow,
    updateFlow,
  ]);

  const handleInvoiceYes = useCallback(() => {
    if (!flow) return;
    setInvoiceState({
      open: true,
      appointment: { ...flow.appointment, paymentMethod: flow.method || "cash" },
      amount: parseFloat(flow.amount) || flow.appointment.actualPaidAmount,
      paymentMethod: flow.method || "cash",
    });
    closeFlow();
  }, [flow, closeFlow]);

  const handleInvoiceNo = useCallback(() => {
    closeFlow();
  }, [closeFlow]);

  const handleInvoiceSuccess = useCallback(
    async (invoiceData) => {
      const target = invoiceState.appointment;
      if (!target) return;
      const invoiceRef = {
        documentId: invoiceData.documentId,
        invoiceNumber: invoiceData.invoiceNumber,
        invoiceUrl: invoiceData.invoiceUrl,
        invoiceProvider: invoiceData.invoiceProvider,
        createdAt: new Date().toISOString(),
      };

      try {
        await updateAppointment({
          clinicId,
          appointmentId: target.id,
          appointmentData: {
            isProducedReceipitForThisMeeting: true,
            RecepitsDocsRefArray: [
              ...(target.RecepitsDocsRefArray || []),
              invoiceRef,
            ],
          },
          therapistId,
          therapistName,
          patientName: target.patientName,
        }).unwrap();
      } catch (error) {
        console.error("[CK-3412] Error linking invoice to cancelled appt:", {
          errorCode: "CK-3412",
          errorMessage: error.message,
          appointmentId: target.id,
          timestamp: new Date().toISOString(),
        });
      }
    },
    [clinicId, invoiceState.appointment, therapistId, therapistName, updateAppointment]
  );

  const handleInvoiceClose = useCallback(() => {
    setInvoiceState({
      open: false,
      appointment: null,
      amount: null,
      paymentMethod: null,
    });
  }, []);

  const cancelFlowNode = useMemo(() => {
    return (
      <>
        {flow && (
          <FlowModal
            flow={flow}
            updateFlow={updateFlow}
            onClose={closeFlow}
            onConfirmCancel={handleConfirmCancel}
            onPostponeWeek={handlePostponeWeek}
            onTxKeep={handleTxKeep}
            onTxConvertToFee={handleTxConvertToFee}
            onTxCancel={handleTxCancel}
            onFeeYes={handleFeeYes}
            onFeeNo={handleFeeNo}
            onAmountConfirm={handleAmountConfirm}
            onDetailsConfirm={handleDetailsConfirm}
            onDetailsPendingCollection={handleDetailsPendingCollection}
            onInvoiceYes={handleInvoiceYes}
            onInvoiceNo={handleInvoiceNo}
            canCreateInvoices={canCreateInvoices}
          />
        )}
        {isDigitalInvoiceActive ? (
          <DigitalInvoiceQuickModal
            isOpen={invoiceState.open}
            onClose={handleInvoiceClose}
            appointment={invoiceState.appointment}
            patientName={invoiceState.appointment?.patientName}
            amount={invoiceState.amount}
            paymentMethod={invoiceState.paymentMethod}
            onSuccess={handleInvoiceSuccess}
          />
        ) : (
          <InvoiceQuickModal
            isOpen={invoiceState.open}
            onClose={handleInvoiceClose}
            appointment={invoiceState.appointment}
            patientName={invoiceState.appointment?.patientName}
            amount={invoiceState.amount}
            paymentMethod={invoiceState.paymentMethod}
            onSuccess={handleInvoiceSuccess}
          />
        )}
      </>
    );
  }, [
    flow,
    updateFlow,
    closeFlow,
    handleConfirmCancel,
    handlePostponeWeek,
    handleTxKeep,
    handleTxConvertToFee,
    handleTxCancel,
    handleFeeYes,
    handleFeeNo,
    handleAmountConfirm,
    handleDetailsConfirm,
    handleDetailsPendingCollection,
    handleInvoiceYes,
    handleInvoiceNo,
    canCreateInvoices,
    isDigitalInvoiceActive,
    invoiceState,
    handleInvoiceClose,
    handleInvoiceSuccess,
  ]);

  return {
    startCancel,
    startPostpone,
    isCancelling,
    cancelFlowNode,
  };
}

// ==========================================
// Internal modal UI
// ==========================================

function FlowModal({
  flow,
  updateFlow,
  onClose,
  onConfirmCancel,
  onPostponeWeek,
  onTxKeep,
  onTxConvertToFee,
  onTxCancel,
  onFeeYes,
  onFeeNo,
  onAmountConfirm,
  onDetailsConfirm,
  onDetailsPendingCollection,
  onInvoiceYes,
  onInvoiceNo,
  canCreateInvoices,
}) {
  const {
    step,
    appointment,
    amount,
    method,
    date,
    isWorking,
    error,
    reason,
    reasonOpen,
    linkedTransaction,
    linkedTreatment,
    seriesMeetings,
    postponeConflicts,
    postponeResult,
    postponeOnly,
  } = flow;
  const whenStr = formatAppointmentWhen(appointment);
  const isPreloading =
    linkedTransaction === undefined ||
    linkedTreatment === undefined ||
    seriesMeetings === undefined;
  const isSeries = appointment?.isRecurring === true;
  const postponePlan = isSeries
    ? buildPostponePlan(appointment, seriesMeetings)
    : null;
  const canPostpone = !!postponePlan?.isPossible;
  const hasInvoice =
    Array.isArray(appointment?.RecepitsDocsRefArray) &&
    appointment.RecepitsDocsRefArray.length > 0;

  const handleOverlayClick = (e) => {
    if (isWorking) return;
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="cancel-flow-overlay" onClick={handleOverlayClick}>
      <div className="cancel-flow-modal" onClick={(e) => e.stopPropagation()}>
        <ModalCloseButton
          onClick={(e) => {
            e.stopPropagation();
            if (!isWorking) onClose();
          }}
        />

        {step === CANCEL_STEPS.CONFIRM && (
          <div className="cancel-flow-step">
            <div className="cancel-flow-icon warning">
              <Warning size={28} weight="duotone" />
            </div>
            <h3 className="cancel-flow-title">ביטול פגישה</h3>
            <p className="cancel-flow-message">
              האם לבטל את הפגישה עם <strong>{appointment.patientName}</strong>
              {whenStr && <> ב-{whenStr}</>}?
            </p>
            <p className="cancel-flow-subtext">
              הפגישה תישאר ביומן מסומנת כ"בוטלה", תוסר מפגישות ממתינות ולא יישלח
              לה דו"ח סיכום.
            </p>

            {/* Series-aware explanation: what cancelling does to the rest of
                the series, and the "push it a week" alternative. */}
            {!isPreloading && isSeries && (
              <div className="cancel-flow-notice info cancel-flow-series">
                <ArrowsClockwise size={18} weight="duotone" />
                <div>
                  <strong>הפגישה היא חלק מסדרת מפגשים.</strong>
                  <ul className="cancel-flow-series-list">
                    <li>
                      המפגש יישאר בסדרה ויסומן כ"בוטל" — הוא לא יימחק ולא ייווצר
                      מפגש חלופי במקומו.
                    </li>
                    <li>
                      שאר המפגשים בסדרה יישארו במועדים שלהם, והסדרה תסתיים באותו
                      תאריך כמו היום.
                    </li>
                    <li>
                      תזכורות SMS שטרם נשלחו עבור המפגש הזה יבוטלו אוטומטית.
                    </li>
                  </ul>
                  {canPostpone ? (
                    <>
                      <span className="cancel-flow-series-alt">
                        לא רוצה לאבד את המפגש? אפשר לדחות אותו בשבוע
                        {postponePlan.laterCount > 0
                          ? ` ולדחות איתו את ${postponePlan.laterCount} המפגשים הבאים`
                          : ""}
                        .
                      </span>
                      <button
                        type="button"
                        className="cancel-flow-btn tertiary cancel-flow-postpone-btn"
                        onClick={() =>
                          updateFlow({
                            step: CANCEL_STEPS.POSTPONE,
                            error: null,
                            postponeConflicts: [],
                          })
                        }
                        disabled={isWorking}
                      >
                        <CalendarPlus size={16} weight="duotone" />
                        דחה בשבוע במקום לבטל
                      </button>
                    </>
                  ) : (
                    <span className="cancel-flow-series-alt muted">
                      דחייה בשבוע אינה זמינה למפגש שכבר עבר.
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Informational notices (treatment summary / linked payment) */}
            {isPreloading && (
              <div className="cancel-flow-notice loading">
                <LoaderLoop size="xs" />
                <span>טוען מידע מקושר…</span>
              </div>
            )}

            {!isPreloading && linkedTreatment && (
              <div className="cancel-flow-notice info">
                <NotePencil size={18} weight="duotone" />
                <div>
                  <strong>קיים סיכום טיפול מקושר לפגישה זו.</strong>
                  <span>
                    {" "}
                    הוא יישאר מקושר למטופל/ת וזמין לצפייה — לא יימחק במהלך
                    הביטול.
                  </span>
                </div>
              </div>
            )}

            {!isPreloading && isPaidFamily(linkedTransaction) && (
              <div className="cancel-flow-notice warning">
                <CurrencyCircleDollar size={18} weight="duotone" />
                <div>
                  <strong>קיים מעקב תשלום ששולם לפגישה זו.</strong>
                  <span>
                    {" "}
                    בשלב הבא נבקש ממך להחליט מה לעשות עם התשלום הקיים
                    {linkedTransaction.amount
                      ? ` על סך ${formatAmount(linkedTransaction.amount)}`
                      : ""}
                    .
                  </span>
                </div>
              </div>
            )}

            {/* Collapsible reason textarea */}
            <div className="cancel-flow-reason">
              <button
                type="button"
                className="cancel-flow-reason-toggle"
                onClick={() => updateFlow({ reasonOpen: !reasonOpen })}
                disabled={isWorking}
              >
                {reasonOpen ? (
                  <CaretUp size={14} weight="bold" />
                ) : (
                  <CaretDown size={14} weight="bold" />
                )}
                <span>הוסף סיבת ביטול (אופציונאלי)</span>
                {!reasonOpen && reason?.trim() && (
                  <span className="cancel-flow-reason-filled-dot" />
                )}
              </button>
              {reasonOpen && (
                <div className="cancel-flow-reason-body">
                  <textarea
                    className="cancel-flow-reason-input"
                    placeholder="לדוגמה: המטופל התקשר ועדכן שלא יגיע, הורה ביטל…"
                    value={reason}
                    maxLength={MAX_REASON_LENGTH}
                    rows={3}
                    onChange={(e) =>
                      updateFlow({ reason: e.target.value.slice(0, MAX_REASON_LENGTH) })
                    }
                    disabled={isWorking}
                  />
                  <div className="cancel-flow-reason-footer">
                    <span className="cancel-flow-reason-hint">
                      הסיבה תוצג בכרטיס הפגישה, ביומן המטופל ובלוג הפעילות.
                    </span>
                    <span className="cancel-flow-reason-count">
                      {(reason || "").length}/{MAX_REASON_LENGTH}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {error && <p className="cancel-flow-error">{error}</p>}
            <div className="cancel-flow-actions">
              <button
                type="button"
                className="cancel-flow-btn secondary"
                onClick={onClose}
                disabled={isWorking}
              >
                חזרה
              </button>
              <button
                type="button"
                className="cancel-flow-btn primary warning"
                onClick={onConfirmCancel}
                disabled={isWorking || isPreloading}
              >
                {isWorking ? (
                  <>
                    <LoaderLoop size="xs" /> מבטל...
                  </>
                ) : (
                  "אישור ביטול"
                )}
              </button>
            </div>
          </div>
        )}

        {step === CANCEL_STEPS.POSTPONE && postponePlan && (
          <div className="cancel-flow-step">
            <div className="cancel-flow-icon info">
              <CalendarPlus size={28} weight="duotone" />
            </div>
            <h3 className="cancel-flow-title">דחיית המפגש בשבוע</h3>
            {/* Entering this step straight from a delete dialog can outrun the
                series load — never state a count we don't have yet. */}
            {isPreloading && (
              <div className="cancel-flow-notice loading">
                <LoaderLoop size="xs" />
                <span>טוען את שאר המפגשים בסדרה…</span>
              </div>
            )}
            <p className="cancel-flow-message">
              המפגש עם <strong>{appointment.patientName}</strong> יעבור מ-
              {formatDateOnly(new Date(appointment.startTime))} ל-
              <strong>
                {formatDateOnly(postponePlan.newStart)} בשעה{" "}
                {formatTimeOnly(postponePlan.newStart)}
              </strong>
              .
            </p>

            <ul
              className="cancel-flow-series-list cancel-flow-plan-list"
              style={isPreloading ? { opacity: 0.5 } : undefined}
            >
              {postponePlan.laterCount > 0 ? (
                <li>
                  <strong>{postponePlan.laterCount} המפגשים הבאים</strong> בסדרה
                  יידחו גם הם בשבוע — כל אחד נשאר באותו יום בשבוע ובאותה שעה.
                </li>
              ) : (
                <li>זהו המפגש האחרון שטרם התקיים בסדרה — רק הוא יזוז.</li>
              )}
              {postponePlan.lastAfter && (
                <li>
                  מספר המפגשים בסדרה לא משתנה. הסדרה תסתיים ב-
                  <strong>{formatDateOnly(postponePlan.lastAfter)}</strong>.
                </li>
              )}
              <li>
                מפגשים שכבר התקיימו, בוטלו או סומנו כהושלמו — לא ישתנו.
              </li>
              <li>
                המטופל יקבל הודעת SMS על המועד החדש עבור המפגש הזה בלבד (אם
                נשלחה לו כבר הודעה על הפגישה), ולא הודעה נפרדת לכל מפגש בסדרה.
              </li>
              <li>
                תזכורות ואישורי הגעה יתוזמנו מחדש אוטומטית לפי המועדים החדשים.
              </li>
              <li>
                תשלומים שכבר שולמו או שהופקה עבורם חשבונית לא ישתנו. צפי ההכנסה
                של כל מפגש יעבור יחד איתו.
              </li>
            </ul>

            {postponeConflicts?.length > 0 && (
              <div className="cancel-flow-notice warning">
                <Warning size={18} weight="duotone" />
                <div>
                  <strong>
                    {`המועדים החדשים מתנגשים עם ${postponeConflicts.length} פגישות קיימות:`}
                  </strong>
                  <ul className="cancel-flow-series-list">
                    {postponeConflicts.slice(0, 3).map((conflict, idx) => {
                      const when = new Date(conflict.date);
                      return (
                        <li key={idx}>
                          {formatDateOnly(when)} בשעה {formatTimeOnly(when)}
                          {conflict.existingAppointment?.patientName
                            ? ` — ${conflict.existingAppointment.patientName}`
                            : ""}
                        </li>
                      );
                    })}
                    {postponeConflicts.length > 3 && (
                      <li>{`ועוד ${postponeConflicts.length - 3}...`}</li>
                    )}
                  </ul>
                </div>
              </div>
            )}

            {error && <p className="cancel-flow-error">{error}</p>}

            <div className="cancel-flow-actions cancel-flow-actions-stack">
              <button
                type="button"
                className="cancel-flow-btn primary"
                onClick={() =>
                  onPostponeWeek({ allowConflicts: postponeConflicts?.length > 0 })
                }
                disabled={isWorking || isPreloading}
              >
                {isWorking ? (
                  <>
                    <LoaderLoop size="xs" /> דוחה...
                  </>
                ) : postponeConflicts?.length > 0 ? (
                  "דחה בכל זאת"
                ) : (
                  "אשר דחייה בשבוע"
                )}
              </button>
              <button
                type="button"
                className="cancel-flow-btn secondary"
                onClick={() =>
                  postponeOnly
                    ? onClose()
                    : updateFlow({
                        step: CANCEL_STEPS.CONFIRM,
                        error: null,
                        postponeConflicts: [],
                      })
                }
                disabled={isWorking}
              >
                <ArrowRight size={16} weight="bold" />
                {postponeOnly ? "חזרה" : "חזרה לביטול המפגש"}
              </button>
            </div>
          </div>
        )}

        {step === CANCEL_STEPS.POSTPONE_DONE && (
          <div className="cancel-flow-step">
            <div className="cancel-flow-icon info">
              <Check size={28} weight="duotone" />
            </div>
            <h3 className="cancel-flow-title">המפגשים נדחו בשבוע</h3>
            <p className="cancel-flow-message">
              המפגש נקבע ל-
              <strong>
                {formatDateOnly(new Date(postponeResult?.newStart))} בשעה{" "}
                {formatTimeOnly(new Date(postponeResult?.newStart))}
              </strong>
              {postponeResult?.shiftedCount > 0
                ? `, ו-${postponeResult.shiftedCount} המפגשים הבאים נדחו איתו בשבוע.`
                : "."}
            </p>
            {postponeResult?.lastAfter && (
              <p className="cancel-flow-subtext">
                הסדרה תסתיים ב-{formatDateOnly(new Date(postponeResult.lastAfter))}.
              </p>
            )}
            <div className="cancel-flow-actions">
              <button
                type="button"
                className="cancel-flow-btn primary"
                onClick={onClose}
              >
                סגור
              </button>
            </div>
          </div>
        )}

        {step === CANCEL_STEPS.TX_DECISION && (
          <div className="cancel-flow-step">
            <div className="cancel-flow-icon warning">
              <CurrencyCircleDollar size={28} weight="duotone" />
            </div>
            <h3 className="cancel-flow-title">מעקב תשלום מקושר</h3>
            <p className="cancel-flow-message">
              לפגישה זו קיים מעקב תשלום בסטטוס{" "}
              <strong>{describeTxStatus(linkedTransaction)}</strong>
              {linkedTransaction?.amount
                ? (
                  <>
                    {" "}על סך{" "}
                    <strong>{formatAmount(linkedTransaction.amount)}</strong>
                  </>
                )
                : null}
              . כיצד ברצונך להתייחס אליו?
            </p>

            {hasInvoice && (
              <div className="cancel-flow-notice warning compact">
                <Receipt size={18} weight="duotone" />
                <div>
                  <strong>הופקה חשבונית עבור התשלום הזה.</strong>
                  <span>
                    {" "}
                    הקישור לחשבונית יישאר כפי שהוא. לביטול/זיכוי חשבונית יש
                    לפעול דרך ספק החשבוניות.
                  </span>
                </div>
              </div>
            )}

            {error && <p className="cancel-flow-error">{error}</p>}

            <div className="cancel-flow-actions cancel-flow-actions-stack">
              <button
                type="button"
                className="cancel-flow-btn primary"
                onClick={onTxKeep}
                disabled={isWorking}
              >
                {isWorking ? (
                  <>
                    <LoaderLoop size="xs" /> שומר...
                  </>
                ) : (
                  "השאר את מעקב התשלום כפי שהוא"
                )}
              </button>
              <button
                type="button"
                className="cancel-flow-btn tertiary"
                onClick={onTxConvertToFee}
                disabled={isWorking}
              >
                <NotePencil size={16} weight="duotone" />
                המר את מעקב התשלום לדמי ביטול
              </button>
              <button
                type="button"
                className="cancel-flow-btn danger-ghost"
                onClick={onTxCancel}
                disabled={isWorking}
              >
                <X size={16} weight="bold" />
                בטל את מעקב התשלום ואל תוסיף מעקב חדש
              </button>
              <button
                type="button"
                className="cancel-flow-btn secondary"
                onClick={() => updateFlow({ step: CANCEL_STEPS.CONFIRM })}
                disabled={isWorking}
              >
                חזרה
              </button>
            </div>

            <p className="cancel-flow-subtext cancel-flow-tx-hint">
              <Info size={14} weight="duotone" />
              <span>
                בחירה ב"השאר כפי שהוא" תשמור את התשלום הקיים ותסמן את הפגישה
                כבוטלה בלבד. "המר לדמי ביטול" שומרת את אותו הסכום ומסמנת אותו
                כדמי ביטול בדוחות הפיננסיים.
              </span>
            </p>
          </div>
        )}

        {step === CANCEL_STEPS.FEE_ASK && (
          <div className="cancel-flow-step">
            <div className="cancel-flow-icon info">
              <CurrencyCircleDollar size={28} weight="duotone" />
            </div>
            <h3 className="cancel-flow-title">דמי ביטול</h3>
            <p className="cancel-flow-message">
              האם לתעד תשלום דמי ביטול עבור הפגישה?
            </p>
            <div className="cancel-flow-actions">
              <button
                type="button"
                className="cancel-flow-btn secondary"
                onClick={onFeeNo}
                disabled={isWorking}
              >
                <X size={16} weight="bold" /> לא
              </button>
              <button
                type="button"
                className="cancel-flow-btn primary"
                onClick={onFeeYes}
                disabled={isWorking}
              >
                <Check size={16} weight="bold" /> כן
              </button>
            </div>
          </div>
        )}

        {step === CANCEL_STEPS.FEE_AMOUNT && (
          <div className="cancel-flow-step">
            <h3 className="cancel-flow-title">סכום דמי הביטול</h3>
            <div className="cancel-flow-amount-row">
              <span className="cancel-flow-currency">₪</span>
              <input
                type="number"
                className="cancel-flow-amount-input"
                placeholder="סכום"
                value={amount}
                onChange={(e) => updateFlow({ amount: e.target.value })}
                autoFocus
                disabled={isWorking}
              />
            </div>
            {error && <p className="cancel-flow-error">{error}</p>}
            <div className="cancel-flow-actions">
              <button
                type="button"
                className="cancel-flow-btn secondary"
                onClick={() => updateFlow({ step: CANCEL_STEPS.FEE_ASK })}
                disabled={isWorking}
              >
                חזרה
              </button>
              <button
                type="button"
                className="cancel-flow-btn primary"
                onClick={onAmountConfirm}
                disabled={isWorking}
              >
                המשך
              </button>
            </div>
          </div>
        )}

        {step === CANCEL_STEPS.FEE_DETAILS && (
          <div className="cancel-flow-step">
            <h3 className="cancel-flow-title">פרטי התשלום</h3>

            <div className="cancel-flow-field">
              <label>תאריך קבלה</label>
              <DatePicker
                className="cancel-flow-date-input"
                value={date || ""}
                onChange={(e) => updateFlow({ date: e.target.value })}
              />
            </div>

            <div className="cancel-flow-field">
              <label>אמצעי תשלום</label>
              <div className="cancel-flow-method-grid">
                {PAYMENT_METHODS.map((pm) => (
                  <button
                    key={pm.value}
                    type="button"
                    className={`cancel-flow-method-btn ${
                      method === pm.value ? "selected" : ""
                    }`}
                    onClick={() => updateFlow({ method: pm.value })}
                    disabled={isWorking}
                  >
                    {pm.label}
                    {pm.subLabel && (
                      <span className="cancel-flow-method-sub">
                        {pm.subLabel}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <p className="cancel-flow-subtext cancel-flow-pending-hint">
              לא קיבלת עדיין את התשלום? ניתן לשמור את דמי הביטול לגבייה במועד
              מאוחר יותר — הם יופיעו ב"ממתין לגבייה".
            </p>

            {error && <p className="cancel-flow-error">{error}</p>}

            <div className="cancel-flow-actions cancel-flow-actions-stack">
              <button
                type="button"
                className="cancel-flow-btn primary"
                onClick={onDetailsConfirm}
                disabled={isWorking}
              >
                {isWorking ? (
                  <>
                    <LoaderLoop size="xs" /> שומר...
                  </>
                ) : (
                  "שמור כשולם"
                )}
              </button>
              <button
                type="button"
                className="cancel-flow-btn tertiary"
                onClick={onDetailsPendingCollection}
                disabled={isWorking}
              >
                <Hourglass size={16} weight="duotone" /> הוסף כממתין לגבייה
              </button>
              <button
                type="button"
                className="cancel-flow-btn secondary"
                onClick={() => updateFlow({ step: CANCEL_STEPS.FEE_AMOUNT })}
                disabled={isWorking}
              >
                חזרה
              </button>
            </div>
          </div>
        )}

        {step === CANCEL_STEPS.FEE_INVOICE && canCreateInvoices && (
          <div className="cancel-flow-step">
            <div className="cancel-flow-icon info">
              <Receipt size={28} weight="duotone" />
            </div>
            <h3 className="cancel-flow-title">חשבונית</h3>
            <p className="cancel-flow-message">
              להפיק חשבונית עבור דמי הביטול?
            </p>
            <div className="cancel-flow-actions">
              <button
                type="button"
                className="cancel-flow-btn secondary"
                onClick={onInvoiceNo}
              >
                <X size={16} weight="bold" /> לא
              </button>
              <button
                type="button"
                className="cancel-flow-btn primary"
                onClick={onInvoiceYes}
              >
                <Check size={16} weight="bold" /> כן
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
